// /////////////////////////////////////////////////////////////
// [ファイル] useWorkspaceAssistant.ts
// [概要] Genkit 駆動の Google Workspace アシスタントを Firestore 書込トリガー方式で
//        実行するカスタムフック。質問を status='queued' 付きでセッションへ登録すると、
//        バックエンド (assistantOnMessageQueued) が検知して AI を実行し、返信を書き戻す。
//        なりすまし対象はサーバーがメッセージの user_id から決めるため userEmail は送らない。
//
//        会話は Firestore のセッション (messages サブコレクション) へ永続化し、
//        本フックは onSnapshot で購読する。推論はサーバー側で完結するため、送信後に
//        ページ遷移しても返信は会話に反映される (応答待ちは status 購読で表示)。
//        貼り付け画像は履歴非保存のため、送信ターンのみローカルに重ねて表示する。
//        停止 (stop) はセッション doc の cancel_request へ発話 id を書き、バックエンドが購読して中断する。
// /////////////////////////////////////////////////////////////
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  collection,
  doc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { ref as storageRef, uploadString, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/firebase';
import { useAuth } from '../firebase/AuthProvider';
import {
  appendSessionMessage,
  type AssistantMessage,
  type AssistantFileAttachment,
  type AssistantMailSource,
} from './useAssistantSessions';

/** Firestore の mail_source (メール自動処理の元メール) を表示用の型へ変換する */
function toMailSource(raw: unknown): AssistantMailSource | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const v = raw as Record<string, unknown>;
  return {
    from: typeof v.from === 'string' ? v.from : '',
    subject: typeof v.subject === 'string' ? v.subject : '',
    date: typeof v.date === 'string' ? v.date : '',
    attachments: Array.isArray(v.attachments) ? v.attachments.filter((a): a is string => typeof a === 'string') : [],
    gmailUrl: typeof v.gmail_url === 'string' ? v.gmail_url : '',
  };
}

// 型は useAssistantSessions が正準定義。後方互換のため再エクスポートする
export type { AssistantMessage } from './useAssistantSessions';

// ⊕ 添付ファイルの送信単位 (dataUrl は "data:<mime>;base64,..." 形式)
export interface AttachedFileInput {
  name: string;
  mimeType: string;
  dataUrl: string;
}

const SESSIONS = 'HozenallCloud_Assistant_Sessions';

interface Callbacks {
  // 新規セッションを作成する (最初の発話を渡し、作成された sessionId を返す)
  onCreateSession: (firstMessage: string) => Promise<string | null>;
  // 後方互換のため残す (タイトルはバックエンドが Firestore へ直接反映するため現在は未使用)
  onTitleGenerated?: (sessionId: string, title: string) => void;
}

function toMillis(value: unknown): number {
  return value instanceof Timestamp ? value.toMillis() : 0;
}

// 貼り付け画像 (data URL) を Storage へ保存し、永続的な閲覧 URL を返す。
// 保存先は本人 (email) × セッション配下。会話を消すまで保持する (恒久保存)。
async function uploadAssistantImages(
  email: string,
  sessionId: string,
  dataUrls: string[]
): Promise<string[]> {
  const ts = Date.now();
  const urls: string[] = [];
  for (let i = 0; i < dataUrls.length; i++) {
    const dataUrl = dataUrls[i];
    // "data:image/png;base64,..." から MIME を取り出し拡張子に使う
    const mime = dataUrl.slice(5, dataUrl.indexOf(';')) || 'image/png';
    const ext = mime.split('/')[1] || 'png';
    const r = storageRef(storage, `assistant-uploads/${email}/${sessionId}/${ts}_${i}.${ext}`);
    await uploadString(r, dataUrl, 'data_url');
    urls.push(await getDownloadURL(r));
  }
  return urls;
}

// ⊕ 添付ファイルを Storage へ保存し、履歴表示用の {name, url} を返す。
// 1件の保存失敗で全体を止めず、失敗分は名前のみ (url なし) で返す。
async function uploadAssistantFiles(
  email: string,
  sessionId: string,
  files: AttachedFileInput[]
): Promise<AssistantFileAttachment[]> {
  const ts = Date.now();
  const results: AssistantFileAttachment[] = [];
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    // Storage パスに使えない文字を除去 (表示名は Firestore 側の name をそのまま使う)
    const safeName = f.name.replace(/[/\\#?%*:|"<>]/g, '_');
    try {
      const r = storageRef(
        storage,
        `assistant-uploads/${email}/${sessionId}/${ts}_${i}_${safeName}`
      );
      await uploadString(r, f.dataUrl, 'data_url');
      results.push({ name: f.name, url: await getDownloadURL(r) });
    } catch (e) {
      console.error('添付ファイルの Storage 保存に失敗しました', e);
      results.push({ name: f.name });
    }
  }
  return results;
}

// 停止要求をセッション doc へ書く。バックエンド (assistantOnMessageQueued) が購読し、該当発話の実行を中断する。
// messages はルール上フロントから更新できないため、本人が更新できるセッション doc を合図に使う
async function requestStop(sessionId: string, messageId: string): Promise<void> {
  await updateDoc(doc(db, SESSIONS, sessionId), {
    cancel_request: { message_id: messageId, requested_at: serverTimestamp() },
  });
}

// 送信ターンの貼り付け画像 (display 専用・Firestore 非保存) を会話へ重ねるための保持単位
interface ImageOverlay {
  sessionId: string;
  text: string;
  images: string[];
}

export const useWorkspaceAssistant = (
  sessionId: string | null,
  callbacks: Callbacks,
  mode: 'chat' | 'report' | 'accounting' = 'chat'
) => {
  const { user } = useAuth();
  const [rawMessages, setRawMessages] = useState<AssistantMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 画像オーバーレイは sessionId で絞るため、セッション切替時のリセットは不要
  const [imageOverlay, setImageOverlay] = useState<ImageOverlay[]>([]);
  // 停止ボタン押下済み (応答待ちが解けるまで「停止中」表示にする)
  const [stopRequested, setStopRequested] = useState(false);
  // 送信準備中 (セッション作成・添付アップロード) に停止された場合、発話の登録直後に停止要求を出す
  const stopAfterQueueRef = useRef(false);

  // コールバックは描画ごとに参照が変わり得るので ref に逃がし、send の依存から外す
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // メッセージを onSnapshot 購読する。バックエンドが書いた返信もリアルタイムに反映される
  // ため、送信後にページ遷移して戻っても会話が最新化される (応答待ちに縛られない)。
  useEffect(() => {
    setError(null);
    if (!sessionId || !user?.email) {
      setRawMessages([]);
      return;
    }
    const email = user.email;
    setRestoring(true);
    // 複合インデックス回避のため where のみ + クライアント側ソート (ルールの user_id 一致も満たす)
    const q = query(
      collection(db, SESSIONS, sessionId, 'messages'),
      where('user_id', '==', email)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((d) => {
            const data = d.data();
            // 送信直後の未確定書込 (serverTimestamp 未解決) は最新として末尾に並べる
            const at = d.metadata.hasPendingWrites
              ? Date.now()
              : toMillis(data.created_at);
            return {
              id: d.id,
              role: data.role as 'user' | 'model',
              text: (data.text as string) ?? '',
              // Storage 保存済みの画像 URL があれば表示に使う (再読込・履歴で再表示)
              images: (data.image_urls as string[] | undefined) ?? undefined,
              // 添付ファイル (名前 + Storage URL) は履歴でチップ表示する
              files:
                (data.file_attachments as AssistantFileAttachment[] | undefined) ??
                undefined,
              // トリガー実行の処理状態 (queued/processing は「応答待ち」表示に使う)
              status: (data.status as AssistantMessage['status']) ?? undefined,
              // 実行ツール名 (評価記録の付帯情報)。user 発話や旧回答には無い
              toolNames: (data.tool_names as string[] | undefined) ?? undefined,
              mailSource: toMailSource(data.mail_source),
              _at: at,
            };
          })
          .sort((a, b) => a._at - b._at)
          .map(({ id, role, text, images, files, status, toolNames, mailSource, _at }) => ({
            id,
            role,
            text,
            images,
            files,
            status,
            toolNames,
            mailSource,
            at: _at || undefined,
          }));
        setRawMessages(list);
        setRestoring(false);
      },
      (err) => {
        console.error('会話の購読に失敗しました', err);
        setError('会話履歴の読み込みに失敗しました');
        setRestoring(false);
      }
    );
    return () => unsub();
  }, [sessionId, user?.email]);

  // 画像オーバーレイを (現在セッションの) user メッセージへ、テキスト一致・出現順で重ねる
  const messages = useMemo<AssistantMessage[]>(() => {
    const overlays = imageOverlay.filter((o) => o.sessionId === sessionId);
    if (overlays.length === 0) return rawMessages;
    const remaining = [...overlays];
    return rawMessages.map((m) => {
      if (m.role !== 'user') return m;
      // 既に Storage の永続 URL を持つメッセージは、貼り付け画像の重ね表示をしない
      if (m.images && m.images.length > 0) return m;
      const idx = remaining.findIndex((o) => o.text === m.text);
      if (idx === -1) return m;
      const [o] = remaining.splice(idx, 1);
      return { ...m, images: o.images };
    });
  }, [rawMessages, imageOverlay, sessionId]);

  // トリガー実行の応答待ち (最後のメッセージが処理中のユーザー発話なら true)。
  // バックエンドが status を done/error へ進める、または model 返信が届くと解除される
  const awaitingReply = useMemo(() => {
    const last = rawMessages[rawMessages.length - 1];
    return (
      !!last &&
      last.role === 'user' &&
      (last.status === 'queued' || last.status === 'processing')
    );
  }, [rawMessages]);

  const send = useCallback(
    async (text: string, images?: string[], files?: AttachedFileInput[]) => {
      const trimmed = text.trim();
      const hasImages = !!images && images.length > 0;
      const hasFiles = !!files && files.length > 0;
      // テキスト・画像・添付ファイルのいずれかがあれば送信可 (応答待ち中は多重送信を防ぐ)
      if (
        (!trimmed && !hasImages && !hasFiles) ||
        sending ||
        restoring ||
        awaitingReply ||
        !user?.email
      )
        return;

      setError(null);
      const email = user.email;
      stopAfterQueueRef.current = false;
      setSending(true);

      try {
        // セッション未確定 (新規) なら先に作成し、その id へ保存していく
        // 添付のみの送信でもタイトル元になるよう、空テキスト時は代替文言を使う
        let activeSessionId = sessionId;
        if (!activeSessionId) {
          activeSessionId = await callbacksRef.current.onCreateSession(
            trimmed || (hasImages ? '画像の確認' : 'ファイルの確認')
          );
        }
        if (!activeSessionId) {
          setError('セッションの作成に失敗しました');
          return;
        }

        // 送信ターンの即時表示用に貼り付け画像 (data URL) をローカル保持する。
        // 並行して Storage へ保存し、永続 URL は Firestore メッセージへ紐付ける。
        let imageUrls: string[] | undefined;
        if (hasImages) {
          setImageOverlay((prev) => [
            ...prev,
            { sessionId: activeSessionId as string, text: trimmed, images: images! },
          ]);
          try {
            imageUrls = await uploadAssistantImages(email, activeSessionId, images!);
          } catch (uploadErr) {
            // 保存に失敗しても会話自体は継続する (画像は今回ターンのみローカル表示)
            console.error('画像の Storage 保存に失敗しました', uploadErr);
          }
        }

        // ⊕ 添付ファイルは Storage へ保存し、名前 + 永続 URL を履歴チップ表示に使う
        let fileAttachments: AssistantFileAttachment[] | undefined;
        if (hasFiles) {
          fileAttachments = await uploadAssistantFiles(email, activeSessionId, files!);
        }

        // ユーザー発話を status='queued' と mode 付きで永続化 (onSnapshot が表示へ反映)。
        // この書込自体が実行トリガーになり、バックエンドの assistantOnMessageQueued が
        // 検知して AI 実行 → 返信を書き戻す。会話履歴もバックエンドが Firestore から復元
        // するため、フロントから送るのは今回の発話と添付のみでよい。
        // user_gid は Chat DM 通知用の Google 数値 ID (Chat のアプリ認証は email 指定不可のため)
        const googleUid = user.providerData.find(
          (p) => p.providerId === 'google.com'
        )?.uid;
        const messageId = await appendSessionMessage(
          activeSessionId,
          email,
          {
            role: 'user',
            text: trimmed,
            imageUrls,
            files: fileAttachments,
          },
          { status: 'queued', mode, ...(googleUid ? { user_gid: googleUid } : {}) }
        );
        // 準備中に停止ボタンが押されていたら、登録した発話へ即座に停止要求を出す
        // (バックエンドが AI を呼ばずに「停止しました」を返す)
        if (stopAfterQueueRef.current) {
          await requestStop(activeSessionId, messageId);
        }
        // 応答待ちは awaitingReply (メッセージの status 購読) が引き継ぐ
      } catch (err: any) {
        setError(err.message || 'アシスタント実行中にエラーが発生しました');
      } finally {
        setSending(false);
      }
    },
    [sending, restoring, awaitingReply, user?.email, sessionId, mode]
  );

  // 応答待ちが解けたら (停止完了・回答完了とも) 停止中の表示を戻す
  useEffect(() => {
    if (!sending && !awaitingReply) setStopRequested(false);
  }, [sending, awaitingReply]);

  // 停止ボタン。応答待ちの発話へ停止要求を出す。送信準備中なら登録直後に出すよう予約する。
  // 実行中のツール操作 (予定登録・ファイル作成など) は完了を待って止まり、取り消しはしない
  const stop = useCallback(async () => {
    if (stopRequested) return;
    if (sending) {
      stopAfterQueueRef.current = true;
      setStopRequested(true);
      return;
    }
    const last = rawMessages[rawMessages.length - 1];
    if (!sessionId || !awaitingReply || !last?.id) return;
    setStopRequested(true);
    try {
      await requestStop(sessionId, last.id);
    } catch (err) {
      console.error('停止要求の送信に失敗しました', err);
      setStopRequested(false);
      setError('停止できませんでした。もう一度お試しください');
    }
  }, [stopRequested, sending, rawMessages, sessionId, awaitingReply]);

  // トリガー方式では書込完了後も推論が続くため、status 購読による応答待ちを loading に合算する
  return {
    messages,
    loading: sending || awaitingReply,
    restoring,
    error,
    send,
    stop,
    stopping: stopRequested,
  };
};
